import React, { useState, useEffect, useCallback } from 'react';
import {
  ChatBubbleBottomCenterTextIcon,
  HomeModernIcon,
  ChatBubbleLeftRightIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { useApi } from "../Api";
import { useNavigate } from 'react-router-dom';
import Modal from '../components/Modal';
import ConversationList from '../components/ConversationList';
import ChatWindow from '../components/ChatWindow';
import { ClientSearchModal } from '../components/ClientSearchModal';
import {
  formatTimestamp,
  groupMessagesByDate,
  formatDateHeader,
} from '../utils/dateFormatting';
import { useIsMobile } from '../hooks/isMobile';
import { getChatHeight } from '../utils/heightCalculations';

import { Conversation, Message } from '../types/Messaging';

interface Family {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  individuals: {
    id: number;
    firstName: string;
    lastName: string;
  }[];
  businesses: { id: number; name: string }[];
  trusts: { id: number; name: string }[];
  bankAccounts: { id: number; name: string }[];
  brokerageCustodians: { id: number; name: string }[];
  charitableOrganizations: { id: number; name: string }[];
  insuranceCompanies: { id: number; name: string }[];
  vendors: { id: number; name: string }[];
  conciergeProfiles: { id: number; name: string }[];
}

const ConciergeDashboard: React.FC = () => {
  const api = useApi();
  const navigate = useNavigate();
  const [families, setFamilies] = useState<Family[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeTab, setActiveTab] = useState<"families" | "conversations">("families");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [familyName, setFamilyName] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Chat logic
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);

  // Client search modal
  const [showClientSearchModal, setShowClientSearchModal] = useState(false);

  // Responsive design
  const isMobile = useIsMobile();

  const populateConversations = useCallback(async () => {
    try {
      const response = await api.get("/api/messaging/getconciergeconversations");
      const conversationsData = response.data?.conversations || [];
      const mappedConversations: Conversation[] = conversationsData.map((c: any) => ({
        ...c,
        status: (() => {
          switch (c.status?.toUpperCase?.()) {
            case "NEW": return 0;
            case "OPEN": return 1;
            case "CLOSED": return 2;
            case "ARCHIVED": return 3;
            default: return 0;
          }
        })(),
        category: (() => {
          switch (c.category?.toUpperCase?.()) {
            case "GENERAL": return 0;
            case "ADDITIONAL_DOCUMENTS": return 1;
            default: return 0;
          }
        })()
      }));
      setConversations(mappedConversations);
    } catch (error: any) {
      navigate("/error", {
        state: {
          error: error.response?.data || "An error occurred while fetching conversations",
          status: error.response?.status || 500,
        },
      });
    }
  }, [api, navigate]);

  useEffect(() => {
    const populateFamilies = async () => {
      try {
        setFamilies([]);
        setLoading(true);
        const response = await api.get("/api/family/getfamilies");
        setFamilies(response.data.families || []);
      } catch (error: any) {
        navigate("/error", {
          state: {
            error: error.response?.data || "An error occurred while fetching families",
            status: error.response?.status,
          },
        });
      } finally {
        setLoading(false);
      }
    };

    const getCurrentUserId = async () => {
      try {
        const userResponse = await api.get('/api/profile/GetLoginProfileId');
        setCurrentUserId(userResponse.data);
      } catch (error: any) { /* ignore */ }
    };

    populateFamilies();
    populateConversations();
    getCurrentUserId();
  }, [api, navigate, populateConversations]);

  useEffect(() => {
    if (!selectedConversation) {
      setMessages([]);
      return;
    }
    const fetchMessages = async () => {
      setMessagesLoading(true);
      try {
        const response = await api.get(`/api/messaging/GetMessages?conversationId=${selectedConversation.id}`);
        setMessages(response.data.messages || []);
      } catch (e) { }
      finally { setMessagesLoading(false); }
    };
    fetchMessages();
  }, [selectedConversation, api]);

  // Poll for conversations
  useEffect(() => {
    const pollForConversations = async () => {
      try {
        const response = await api.get("/api/messaging/getconciergeconversations");
        const conversationsData = response.data?.conversations || [];
        const mappedConversations: Conversation[] = conversationsData.map((c: any) => ({
          ...c,
          status: (() => {
            switch (c.status?.toUpperCase?.()) {
              case "NEW": return 0;
              case "OPEN": return 1;
              case "CLOSED": return 2;
              case "ARCHIVED": return 3;
              default: return 0;
            }
          })(),
          category: (() => {
            switch (c.category?.toUpperCase?.()) {
              case "GENERAL": return 0;
              case "ADDITIONAL_DOCUMENTS": return 1;
              default: return 0;
            }
          })()
        }));
        setConversations(mappedConversations);
      } catch (err) {
        console.error('Error polling for conversations:', err);
      }
    };
    const interval = setInterval(pollForConversations, 5000);
    return () => clearInterval(interval);
  }, [api]);

  // Poll for messages
  useEffect(() => {
    if (!selectedConversation) return;
    const pollForMessages = async () => {
      try {
        const response = await api.get(`/api/messaging/GetMessages?conversationId=${selectedConversation.id}`);
        setMessages(response.data.messages || []);
      } catch (err) {
        console.error('Error polling for messages:', err);
      }
    };
    const interval = setInterval(pollForMessages, 3000);
    return () => clearInterval(interval);
  }, [selectedConversation, api]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((newMessage.trim() || uploadedFiles.length > 0) && selectedConversation && currentUserId) {
      try {
        const formData = new FormData();
        formData.append('conversationId', selectedConversation.id.toString());
        formData.append('senderId', currentUserId.toString());
        if (newMessage.trim()) {
          formData.append('content', newMessage);
        }
        uploadedFiles.forEach((file) => {
          formData.append('attachments', file);
        });
        await api.post('/api/messaging/SendMessage', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const response = await api.get(`/api/messaging/GetMessages?conversationId=${selectedConversation.id}`);
        setMessages(response.data.messages || []);
        const conversationsResponse = await api.get("/api/messaging/getconciergeconversations");
        const conversationsData = conversationsResponse.data?.conversations || [];
        const mappedConversations: Conversation[] = conversationsData.map((c: any) => ({
          ...c,
          status: (() => {
            switch (c.status?.toUpperCase?.()) {
              case "NEW": return 0;
              case "OPEN": return 1;
              case "CLOSED": return 2;
              case "ARCHIVED": return 3;
              default: return 0;
            }
          })(),
          category: (() => {
            switch (c.category?.toUpperCase?.()) {
              case "GENERAL": return 0;
              case "ADDITIONAL_DOCUMENTS": return 1;
              default: return 0;
            }
          })()
        }));
        setConversations(mappedConversations);
        setNewMessage('');
        setUploadedFiles([]);
      } catch (err: any) {
        console.error("Error sending message:", err);
        setError(err.response?.data || "Failed to send message. Please try again.");
      }
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setUploadedFiles(prev => [...prev, ...Array.from(files)]);
      e.target.value = '';
    }
  };

  const handleStartNewConversation = async (loginProfileId: number, familyId: number) => {
    try {
      const response = await api.post('/api/messaging/CreateConciergeConversation', {
        clientLoginProfileId: loginProfileId,
        familyId: familyId
      });

      if (response.data?.conversation) {
        await populateConversations();
        const newConversation = conversations.find(c => c.id === response.data.conversation.id);
        if (newConversation) {
          setSelectedConversation(newConversation);
        }
        setShowClientSearchModal(false);
      }
    } catch (error: any) {
      console.error('Error creating conversation:', error);
      const status = error.response?.status;
      if (status === 500) {
        setError(
          'An unexpected error occurred. If you continue to see this message, please contact system support at support@digitalfamilyoffice.io'
        );
      } else if (status === 400) {
        setError(
          'Please review the following errors and make the required updates: ' +
          (error.response?.data || 'Invalid data submitted.')
        );
      } else {
        setError(error.response?.data || 'Failed to create conversation. Please try again.');
      }
    }
  };

  const handleCreateFamily = async () => {
    if (!familyName.trim()) {
      setError("Family name is required.");
      return;
    }
    if (familyName.length > 255) {
      setError("Family name must be less than 255 characters.");
      return;
    }

    try {
      const response = await api.post("/api/family/addfamily", { familyName });
      const newFamily = response.data.families?.[0];
      if (!newFamily?.id) {
        throw new Error("Failed to get new family ID");
      }
      setFamilies((prev) => [...prev, newFamily]);
      setFamilyName("");
      setShowModal(false);
      navigate(`/family-management/${newFamily.id}`);
    } catch (error: any) {
      console.error("Error creating family:", error);
      setError(error.response?.data || "Failed to create family. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto pt-0 pb-4 px-4 lg:p-6">
      {/* Header Card */}
      <div className="bg-white rounded-xl shadow-md px-4 py-3 mb-3 lg:py-4 lg:mb-4 relative">
        {isMobile ? (
          /* Mobile Layout */
          <div className="flex flex-col space-y-3">
            <div className="flex items-center">
              <div className="w-8 h-8 dfo-bg-gradient-teal-horizontal rounded-full flex items-center justify-center mr-3">
                <HomeModernIcon className="w-5 h-5 text-white" />
              </div>
              <h1 className="dag-bartitle text-xl font-bold text-gray-900">Concierge Dashboard</h1>
            </div>
            {/* Tabs */}
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setActiveTab("families");
                  setSelectedConversation(null);
                }}
                className={`px-4 py-2 rounded-md text-sm ${activeTab === "families"
                  ? "tab-active"
                  : "tab-inactive"
                  }`}
              >
                Families
              </button>
              <button
                onClick={() => setActiveTab("conversations")}
                className={`px-4 py-2 rounded-md text-sm relative ${activeTab === "conversations"
                  ? "tab-active"
                  : "tab-inactive"
                  }`}
              >
                Conversations
                {(() => {
                  const newConversationCount = conversations.filter(conversation => conversation.status === 0).length;
                  return newConversationCount > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-500 text-white">
                      {newConversationCount}
                    </span>
                  );
                })()}
              </button>
            </div>
          </div>
        ) : (
          /* Desktop Layout */
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-8 h-8 dfo-bg-gradient-teal-horizontal rounded-full flex items-center justify-center mr-3">
                <HomeModernIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="flex items-center">
                  <h1 className="dag-bartitle text-2xl font-bold text-gray-900">Concierge Dashboard</h1>
                </div>
                <p className="text-gray-600 text-sm">Manage families and conversations</p>
              </div>
            </div>
            {/* Tabs */}
            <div className="flex space-x-6">
              <button
                onClick={() => {
                  setActiveTab("families");
                  setSelectedConversation(null);
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-md ${activeTab === "families"
                  ? "tab-active"
                  : "tab-inactive"
                  }`}
              >
                <HomeModernIcon className="w-5 h-5" />
                Families
              </button>
              <button
                onClick={() => setActiveTab("conversations")}
                className={`flex items-center gap-2 px-4 py-2 rounded-md relative ${activeTab === "conversations"
                  ? "tab-active"
                  : "tab-inactive"
                  }`}
              >
                <ChatBubbleBottomCenterTextIcon className="w-5 h-5" />
                Conversations
                {(() => {
                  const newConversationCount = conversations.filter(conversation => conversation.status === 0).length;
                  return newConversationCount > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-500 text-white">
                      {newConversationCount}
                    </span>
                  );
                })()}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Families Tab */}
      {activeTab === "families" && (
        <div className="space-y-6">
          {families.length === 0 ? (
            <p className="text-gray-600 text-sm">No families available.</p>
          ) : (
            families.map((family) => (
              <div
                key={family.id}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition"
              >
                <div className="flex justify-between items-start">
                  <h3 className="text-lg font-semibold text-gray-800">{family.name}</h3>
                  <button
                    onClick={() => navigate(`/family-management/${family.id}`)}
                    className="btn-primary text-center"
                  >
                    Manage Family
                  </button>
                </div>
                <p className="text-gray-500 text-sm mt-2">
                  Created: {new Date(family.createdAt).toLocaleDateString()}
                </p>
                <p className="text-gray-700 mt-2">
                  {family.individuals.length} Individuals, {family.businesses.length} Businesses
                </p>
              </div>
            ))
          )}

          <div className="flex justify-center mt-6">
            <button
              onClick={() => setShowModal(true)}
              className="btn-primary text-center"
            >
              Add New Family
            </button>
          </div>
        </div>
      )}

      {/* Conversations Tab */}
      {activeTab === "conversations" && (
        <div
          className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6"
          style={{ height: getChatHeight(isMobile) }}
        >
          {/* Conversations Card (with built-in status/family filtering and conversation list) */}
          <div className={`lg:col-span-6 h-full min-h-0 ${selectedConversation && isMobile ? 'hidden' : 'block'}`}>
            <ConversationList
              conversations={conversations}
              families={families.filter(family =>
                conversations.some(conversation => conversation.familyId === family.id)
              )}
              onSelectConversation={(conv) => {
                // If clicking the same conversation that's already selected, deselect it
                if (selectedConversation && selectedConversation.id === conv.id) {
                  setSelectedConversation(null);
                } else {
                  setSelectedConversation(conv);
                  if (isMobile) window.scrollTo(0, 0);
                }
              }}
              onConversationArchived={async () => {
                // Refresh conversations when one is archived/unarchived
                await populateConversations();
              }}
              onConversationDeleted={async () => {
                // Refresh conversations when one is deleted
                await populateConversations();
              }}
              showDate={true}
              selectedConversation={selectedConversation}
              onNewConversation={() => setShowClientSearchModal(true)}
            />
          </div>

          {/* Chat Area Card */}
          <div className={`lg:col-span-6 flex flex-col h-full min-h-0 bg-white rounded-xl shadow-md overflow-hidden ${selectedConversation || !isMobile ? 'flex' : 'hidden lg:flex'}`}>
            {selectedConversation && isMobile && (
              <div className="p-4 border-b border-gray-200 flex-shrink-0 flex items-center">
                <button
                  onClick={() => setSelectedConversation(null)}
                  className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors mr-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div className="w-6 h-6 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
                  <UserCircleIcon className="w-4 h-4 text-gray-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 ml-2 truncate">
                  {selectedConversation?.otherParticipantName || `Conversation ${selectedConversation?.id}`}
                </h2>
              </div>
            )}
            <div className="flex-1 min-h-0 flex flex-col">
              {selectedConversation ? (
                <ChatWindow
                  selectedConv={selectedConversation}
                  messages={messages}
                  messagesLoading={messagesLoading}
                  currentUserId={currentUserId}
                  newMessage={newMessage}
                  setNewMessage={setNewMessage}
                  handleSendMessage={handleSendMessage}
                  handleFileUpload={handleFileUpload}
                  uploadedFiles={uploadedFiles}
                  setUploadedFiles={setUploadedFiles}
                  isMobile={isMobile}
                  formatTimestamp={formatTimestamp}
                  formatDateHeader={formatDateHeader}
                  groupMessagesByDate={groupMessagesByDate}
                  showHeader={!isMobile}
                />
              ) : (
                <div className="flex-1 flex items-center justify-center h-full w-full min-h-0">
                  <div className="text-center opacity-80">
                    <ChatBubbleLeftRightIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Select a conversation
                    </h3>
                    <p className="text-gray-600">
                      Choose a conversation from the sidebar to start messaging
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <Modal onClose={() => setShowModal(false)}>
          <h3 className="text-lg font-semibold mb-4">Create New Family</h3>
          <input
            type="text"
            placeholder="Enter family name"
            value={familyName}
            onChange={(e) => {
              setFamilyName(e.target.value);
              if (error) setError(null);
            }}
            className="w-full p-2 border rounded mb-4"
          />
          {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
          <div className="flex justify-end space-x-2">
            <button
              onClick={() => setShowModal(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateFamily}
              className="btn-primary"
            >
              Create
            </button>
          </div>
        </Modal>
      )}

      {/* Client Search Modal */}
      <ClientSearchModal
        isOpen={showClientSearchModal}
        onClose={() => setShowClientSearchModal(false)}
        onSelectClient={handleStartNewConversation}
      />
    </div>
  );
};

export default ConciergeDashboard;